/**
 * Printer Utilities — silent-only POS / facture printing (no preview/dialog).
 * Role-aware: receipt / document / label (see domain/print).
 *
 * Recovery is role-strict: never switch a failed job to a random first-physical printer.
 */

import printService from '../services/printService';
import { kvGet, kvRemove } from '../infrastructure/storage/kvAccess';
import {
  isVirtualPrinterEntry,
  filterPhysicalPrinters,
  pickPreferredPhysicalPrinter
} from './printerAvailability';
import {
  printKindToRole,
  resolveAssignedDeviceNameForRole,
  setPrinterRoleAssignment,
  PRINT_ROLE
} from '../domain/print';
import {
  resolveDeviceNameAgainstPrinters,
  recordVerifiedPrinterDevice,
  findPrinterByDeviceIdLoose
} from './printerDeviceAliases';
import { enrichPrintFailureResult } from './printFailureClassify';

function readLegacyPrinterString(key) {
  try {
    const v = kvGet(key);
    if (v == null || v === '') return null;
    return String(v).trim() || null;
  } catch {
    return null;
  }
}

export const getSavedPrinter = () => readLegacyPrinterString('printerDeviceName');

export const getSavedPrinterDisplayName = () =>
  readLegacyPrinterString('printerDisplayName');

export const hasSavedPrinter = () => Boolean(getSavedPrinter());

/** Role-scoped resolve cache: role → { at, deviceName } */
const resolvedByRole = new Map();
/** In-flight discover promises keyed by role (avoids cross-role race). */
const resolvePrinterPromises = new Map();
const PRINTER_CACHE_TTL_MS = 30_000;

/** role → probe result cache */
const physicalProbeByKey = new Map();
const PHYSICAL_PROBE_TTL_MS = 8_000;

function cacheKeyForRole(role) {
  return role || PRINT_ROLE.RECEIPT;
}

function printerDeviceIdFromMatch(match, fallback) {
  return String(match?.deviceName || match?.name || fallback || '').trim() || null;
}

function getCachedDevice(role) {
  const entry = resolvedByRole.get(cacheKeyForRole(role));
  if (!entry) return null;
  if (Date.now() - entry.at >= PRINTER_CACHE_TTL_MS) return null;
  return entry.deviceName || null;
}

function setCachedDevice(role, deviceName) {
  if (!deviceName) return;
  resolvedByRole.set(cacheKeyForRole(role), { at: Date.now(), deviceName });
}

export function invalidateResolvedPrinterCache() {
  resolvedByRole.clear();
  physicalProbeByKey.clear();
}

/**
 * Resolve intended device for a role without auto-discovering Windows default.
 * @param {string} role
 * @param {{ deviceName?: string }} [opts]
 * @returns {string|null}
 */
export function resolveRoleTargetDeviceName(role, opts = {}) {
  const explicit = String(opts.deviceName || '').trim();
  if (explicit && !isVirtualPrinterEntry({ name: explicit, deviceName: explicit })) {
    return explicit;
  }
  // No receipt cross-fallback for document/label (policy default).
  const assigned = resolveAssignedDeviceNameForRole(role);
  if (assigned && !isVirtualPrinterEntry({ name: assigned, deviceName: assigned })) {
    return assigned;
  }
  return getCachedDevice(role);
}

/**
 * Assigned role printer → cache → Windows default / first physical (role-scoped).
 * Never auto-writes role assignments (Settings / PrinterPicker only).
 * Force only skips the short-lived cache when rediscovering; assignment always wins.
 * @param {{ force?: boolean, role?: string }} [options]
 */
export async function resolvePrinterDeviceName(options = {}) {
  const force = options.force === true;
  const role = options.role || PRINT_ROLE.RECEIPT;
  const roleKey = cacheKeyForRole(role);

  const assigned = resolveAssignedDeviceNameForRole(role);
  if (assigned && !isVirtualPrinterEntry({ name: assigned, deviceName: assigned })) {
    // Explicit role target always wins — force must not switch to first Windows queue.
    setCachedDevice(role, assigned);
    return assigned;
  }

  // Clean virtual leftover on legacy receipt keys only.
  const saved = getSavedPrinter();
  if (saved && isVirtualPrinterEntry({ name: saved, deviceName: saved })) {
    try {
      kvRemove('printerDeviceName');
      kvRemove('printerDisplayName');
      setPrinterRoleAssignment(PRINT_ROLE.RECEIPT, null);
    } catch {
      /* ignore */
    }
    invalidateResolvedPrinterCache();
  }

  if (!force) {
    const cached = getCachedDevice(role);
    if (cached) return cached;
  }

  const inflight = resolvePrinterPromises.get(roleKey);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      // Electron default queue is often thermal POS — skip blind default for
      // document/label so A4 does not hit the cash drawer printer.
      const mayUseElectronDefault = role === PRINT_ROLE.RECEIPT;
      if (
        mayUseElectronDefault &&
        typeof window !== 'undefined' &&
        window.electronAPI?.getDefaultPrinter
      ) {
        try {
          const ipcResult = await window.electronAPI.getDefaultPrinter();
          if (ipcResult?.success && ipcResult.deviceName) {
            const deviceName = ipcResult.deviceName;
            if (!isVirtualPrinterEntry({ name: deviceName, deviceName })) {
              // Session cache only — never persist without user action.
              setCachedDevice(role, deviceName);
              return deviceName;
            }
          }
        } catch (ipcError) {
          console.warn(
            '[printerUtils] getDefaultPrinter unavailable, falling back to listPrinters:',
            ipcError?.message || ipcError
          );
        }
      }

      const list = await printService.listPrinters();
      const printers = list?.printers || [];
      const preferredHint =
        assigned ||
        (role === PRINT_ROLE.RECEIPT ? getSavedPrinter() : null) ||
        null;
      const match = pickPreferredPhysicalPrinter(printers, preferredHint, { role });

      const deviceName = match?.name || match?.deviceName || null;
      if (deviceName) {
        setCachedDevice(role, deviceName);
      }
      return deviceName;
    } finally {
      resolvePrinterPromises.delete(roleKey);
    }
  })();

  resolvePrinterPromises.set(roleKey, promise);
  return promise;
}

/**
 * Probe physical connectivity for a role's target (or any physical when no target).
 * @param {{ force?: boolean, role?: string, deviceName?: string }} [options]
 */
export async function probePhysicalPrinterConnected(options = {}) {
  const force = options.force === true;
  const role = options.role || null;
  const preferred =
    String(options.deviceName || '').trim() ||
    (role ? resolveAssignedDeviceNameForRole(role) : null) ||
    (role === PRINT_ROLE.RECEIPT || !role ? getSavedPrinter() : null) ||
    null;
  const cacheKey = preferred
    ? `dev:${preferred}`
    : role
      ? `role:${role}`
      : 'any';

  if (!force) {
    const cached = physicalProbeByKey.get(cacheKey);
    if (cached && Date.now() - cached.at < PHYSICAL_PROBE_TTL_MS) {
      return {
        connected: cached.connected,
        deviceName: cached.deviceName,
        printerName: cached.printerName,
        role: role || null
      };
    }
  }

  if (typeof window === 'undefined' || !window.electronAPI) {
    const empty = {
      connected: false,
      deviceName: null,
      printerName: null,
      role: role || null
    };
    physicalProbeByKey.set(cacheKey, { at: Date.now(), ...empty });
    return empty;
  }

  try {
    const list = await printService.listPrinters();
    const printers = filterPhysicalPrinters(list?.printers || []);

    if (preferred) {
      const match = findPrinterByDeviceIdLoose(printers, preferred);
      const result = {
        connected: Boolean(match),
        deviceName: match ? printerDeviceIdFromMatch(match, preferred) : null,
        printerName: match
          ? match.displayName || match.name || preferred
          : null,
        role: role || null,
        preferred
      };
      physicalProbeByKey.set(cacheKey, { at: Date.now(), ...result });
      return result;
    }

    // No preferred device: any physical printer qualifies (status banner / warm).
    if (window.electronAPI.getPhysicalPrinterStatus) {
      const status = await window.electronAPI.getPhysicalPrinterStatus();
      const connected = Boolean(status?.success && status?.connected && status?.deviceName);
      const result = {
        connected,
        deviceName: connected ? status.deviceName : null,
        printerName: connected ? status.printerName || status.deviceName : null,
        role: role || null
      };
      physicalProbeByKey.set(cacheKey, { at: Date.now(), ...result });
      return result;
    }

    const match = pickPreferredPhysicalPrinter(printers, null);
    const result = {
      connected: Boolean(match),
      deviceName: match?.name || match?.deviceName || null,
      printerName: match?.displayName || match?.name || null,
      role: role || null
    };
    physicalProbeByKey.set(cacheKey, { at: Date.now(), ...result });
    return result;
  } catch {
    const empty = {
      connected: false,
      deviceName: null,
      printerName: null,
      role: role || null
    };
    physicalProbeByKey.set(cacheKey, { at: Date.now(), ...empty });
    return empty;
  }
}

/**
 * Warm silent-print pool only when a physical printer is attached.
 * No-ops (and skips window creation) when offline / PDF-only / no device.
 */
export async function warmSilentPrintPipeline(options = {}) {
  if (typeof window === 'undefined' || !window.electronAPI) {
    return { warmed: false, reason: 'no_electron' };
  }
  try {
    const role = options.role || printKindToRole(options.kind);
    const probe = await probePhysicalPrinterConnected({
      force: options.forceProbe === true,
      role
    });
    if (!probe.connected) {
      return { warmed: false, reason: 'no_physical_printer', ...probe };
    }
    if (window.electronAPI.warmSilentPrint) {
      await window.electronAPI.warmSilentPrint(options);
    }
    await resolvePrinterDeviceName({ role });
    return { warmed: true, ...probe };
  } catch {
    return { warmed: false, reason: 'warm_failed' };
  }
}

/**
 * One recovery attempt: same role target only — never switch to a random queue.
 * @param {{
 *   role: string,
 *   failedDeviceName?: string|null,
 *   explicitDeviceName?: string|null
 * }} args
 * @returns {Promise<{ deviceName: string|null, mode: 'retry_same'|'rediscover'|'none' }>}
 */
export async function resolveRoleStrictRecoveryDevice({
  role,
  failedDeviceName = null,
  explicitDeviceName = null
} = {}) {
  const assigned = resolveAssignedDeviceNameForRole(role);
  const explicit = String(explicitDeviceName || '').trim();

  // Fixed target (assignment or call-site override): only retry that device.
  if (assigned && !isVirtualPrinterEntry({ name: assigned, deviceName: assigned })) {
    return { deviceName: assigned, mode: 'retry_same' };
  }
  if (explicit && !isVirtualPrinterEntry({ name: explicit, deviceName: explicit })) {
    return { deviceName: explicit, mode: 'retry_same' };
  }
  if (
    failedDeviceName &&
    !isVirtualPrinterEntry({ name: failedDeviceName, deviceName: failedDeviceName })
  ) {
    // Auto path that just tried a discovered device — allow rediscover for this role only.
    const rediscovered = await resolvePrinterDeviceName({ force: true, role });
    return {
      deviceName: rediscovered || null,
      mode: rediscovered ? 'rediscover' : 'none'
    };
  }

  const rediscovered = await resolvePrinterDeviceName({ force: true, role });
  return {
    deviceName: rediscovered || null,
    mode: rediscovered ? 'rediscover' : 'none'
  };
}

async function resolveLivePrintDeviceName(preferredId) {
  const preferred = String(preferredId || '').trim();
  if (!preferred) return null;
  try {
    const list = await printService.listPrinters();
    const printers = filterPhysicalPrinters(list?.printers || []);
    const resolved = resolveDeviceNameAgainstPrinters(preferred, printers);
    return resolved.deviceName || preferred;
  } catch {
    return preferred;
  }
}

/**
 * @param {string} html
 * @param {string|object} [margins='default']
 * @param {{
 *   kind?: 'thermal'|'document'|'label-sheet',
 *   role?: string,
 *   deviceName?: string,
 *   timeoutMs?: number,
 *   retries?: number,
 *   imageWaitMs?: number
 * }} [printOptions]
 */
export const printSilentToSavedPrinter = async (
  html,
  margins = 'default',
  printOptions = {}
) => {
  const kind =
    printOptions.kind === 'document'
      ? 'document'
      : printOptions.kind === 'label-sheet'
        ? 'label-sheet'
        : 'thermal';
  const role = printOptions.role || printKindToRole(kind);
  const mainRetries =
    printOptions.retries != null ? Math.max(0, Number(printOptions.retries)) : 1;
  const explicitDevice = String(printOptions.deviceName || '').trim() || null;

  let deviceName = resolveRoleTargetDeviceName(role, {
    deviceName: explicitDevice
  });

  if (!deviceName) {
    deviceName = await resolvePrinterDeviceName({ role });
  }

  if (!deviceName) {
    return enrichPrintFailureResult(
      {
        success: false,
        error: 'no_physical_printer',
        message: 'Connectez une imprimante avant d\'imprimer',
        connected: false
      },
      { role, deviceName: null }
    );
  }

  const preferredDevice = deviceName;
  deviceName = (await resolveLivePrintDeviceName(deviceName)) || deviceName;

  let lastResult = null;
  try {
    lastResult = await printService.printSilentHTML(html, deviceName, margins, {
      ...printOptions,
      kind,
      retries: mainRetries
    });
    if (lastResult?.success) {
      setCachedDevice(role, deviceName);
      recordVerifiedPrinterDevice(deviceName, { preferred: preferredDevice });
      return { ...lastResult, role, deviceName, preferredDevice };
    }
  } catch (error) {
    lastResult = {
      success: false,
      error: error.message,
      failureReason: error.message,
      message: 'Failed to print',
      role,
      deviceName
    };
  }

  // Role-strict recovery — never print to a random foreign queue.
  invalidateResolvedPrinterCache();
  const recovery = await resolveRoleStrictRecoveryDevice({
    role,
    failedDeviceName: preferredDevice,
    explicitDeviceName: explicitDevice
  });
  if (!recovery.deviceName) {
    return enrichPrintFailureResult(
      lastResult || {
        success: false,
        error: 'no_physical_printer',
        connected: false
      },
      { role, deviceName: preferredDevice }
    );
  }

  let recoveryDevice =
    (await resolveLivePrintDeviceName(recovery.deviceName)) || recovery.deviceName;

  try {
    lastResult = await printService.printSilentHTML(html, recoveryDevice, margins, {
      ...printOptions,
      kind,
      retries: 0
    });
    if (lastResult?.success) {
      setCachedDevice(role, recoveryDevice);
      recordVerifiedPrinterDevice(recoveryDevice, {
        preferred: preferredDevice || recovery.deviceName
      });
      return {
        ...lastResult,
        role,
        deviceName: recoveryDevice,
        recoveryMode: recovery.mode,
        preferredDevice
      };
    }
  } catch (error) {
    lastResult = {
      success: false,
      error: error.message,
      failureReason: error.message,
      message: 'Failed to print',
      role,
      deviceName: recoveryDevice
    };
  }

  return enrichPrintFailureResult(
    lastResult || {
      success: false,
      error: 'Failed to print',
      message: 'Failed to print'
    },
    { role, deviceName: recoveryDevice || preferredDevice }
  );
};

/** Always silent — never opens preview or system print dialog. */
export const printSilentFast = async (html, options = {}) => {
  const { margins: marginsOption, ...printOptions } = options || {};
  const kind =
    printOptions.kind === 'document'
      ? 'document'
      : printOptions.kind === 'label-sheet'
        ? 'label-sheet'
        : 'thermal';
  // Explicit role wins; otherwise map kind → role (callers can override e.g. A5 receipt).
  const role = printOptions.role || printKindToRole(kind);
  const margins =
    marginsOption !== undefined
      ? marginsOption
      : kind === 'label-sheet'
        ? 'default'
        : { marginType: 'none' };
  return printSilentToSavedPrinter(html, margins, {
    retries: kind === 'thermal' ? 0 : 1,
    imageWaitMs: 0,
    timeoutMs: kind === 'thermal' ? 5000 : kind === 'document' ? 10000 : 12000,
    ...printOptions,
    kind,
    role
  });
};

/**
 * Print a verification page for a print role (settings test).
 * @param {string} role
 * @param {{ language?: string }} [options]
 */
export async function printPrinterRoleTest(role, options = {}) {
  const { buildPrinterRoleTestHtml, roleToPrintKind, getPrinterRoleAssignment } =
    await import('../domain/print');
  const assignment = getPrinterRoleAssignment(role);
  const kind = roleToPrintKind(role);
  const html = buildPrinterRoleTestHtml(role, {
    ...assignment,
    language: options.language
  });
  return printSilentFast(html, {
    kind,
    role,
    deviceName: assignment?.deviceName,
    margins: kind === 'document' ? { marginType: 'printableArea' } : { marginType: 'none' },
    pageWidthMm: kind === 'thermal' ? 72 : kind === 'label-sheet' ? 50 : undefined,
    pageHeightMm: kind === 'label-sheet' ? 30 : undefined
  });
}

/** @deprecated Use printSilentFast — kept for call-site compatibility. */
export const printWithPreviewOrSilent = printSilentFast;

export const showPrinterPicker = async (onPrinterSelected, onClose) => {
  try {
    const { default: PrinterPicker } = await import('../components/PrinterPicker');

    const container = document.createElement('div');
    container.id = 'printer-picker-container';
    document.body.appendChild(container);

    const React = await import('react');
    const ReactDOM = await import('react-dom/client');

    const root = ReactDOM.createRoot(container);

    const handleClose = () => {
      root.unmount();
      document.body.removeChild(container);
      if (onClose) onClose();
    };

    root.render(
      React.createElement(PrinterPicker, {
        onPrinterSelected: (printer) => {
          const deviceName = printer?.deviceName || printer?.name || null;
          if (deviceName) setCachedDevice(PRINT_ROLE.RECEIPT, deviceName);
          if (onPrinterSelected) onPrinterSelected(printer);
        },
        onClose: handleClose
      })
    );
  } catch (error) {
    console.error('Error showing printer picker:', error);
    if (onClose) onClose();
  }
};
